import {
  getCurrentSessionId,
  getIdToken,
  getTestModeHeaders,
  triggerForceSignOut,
} from './auth/authState';

let consecutive401 = 0;
const MAX_CONSECUTIVE_401 = 3;

async function buildAuthHeaders(forceRefresh: boolean): Promise<Record<string, string>> {
  const token = await getIdToken(forceRefresh);
  const sessionId = getCurrentSessionId();
  const headers: Record<string, string> = {
    'X-Session-Id': sessionId,
    ...getTestModeHeaders(),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

export interface AuthFetchInit extends Omit<RequestInit, 'headers' | 'body'> {
  headers?: Record<string, string>;
  json?: unknown;
}

export async function authFetch(input: string, init: AuthFetchInit = {}): Promise<Response> {
  const { json, headers: callerHeaders = {}, ...rest } = init;
  const baseHeaders = await buildAuthHeaders(false);

  const headers: Record<string, string> = { ...baseHeaders, ...callerHeaders };
  let body: BodyInit | undefined;
  if (json !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(json);
  }

  let res = await fetch(input, { ...rest, headers, body });

  if (res.status === 401) {
    const refreshed = await buildAuthHeaders(true);
    res = await fetch(input, { ...rest, headers: { ...refreshed, ...callerHeaders }, body });
  }

  if (res.status === 401) {
    consecutive401 += 1;
    if (consecutive401 >= MAX_CONSECUTIVE_401) {
      triggerForceSignOut('Too many 401s in a row');
    }
  } else {
    consecutive401 = 0;
  }

  return res;
}

export async function authFetchJson<T>(input: string, init: AuthFetchInit = {}): Promise<T> {
  const res = await authFetch(input, init);
  if (!res.ok) {
    let errorBody: unknown = null;
    try {
      errorBody = await res.json();
    } catch {
      // res.json() consumes the body even when parsing fails, so res.text()
      // here can throw "body already read". That's expected; warn rather
      // than swallow so genuinely weird failures (network mid-read) surface.
      try {
        errorBody = await res.text();
      } catch (textErr) {
        console.warn(`Failed to read error body for ${input}:`, textErr);
      }
    }
    throw new ApiError(res.status, input, errorBody);
  }
  return (await res.json()) as T;
}

/**
 * Pulls a short error code out of the server's response body. Routes
 * conventionally return `{ error: 'snake_case_reason' }` on failure; we
 * surface that string so callers can format meaningful messages instead
 * of leaking raw URLs at the user.
 */
function extractErrorCode(body: unknown): string | null {
  if (body && typeof body === 'object' && 'error' in body) {
    const e = (body as { error?: unknown }).error;
    if (typeof e === 'string' && e.length > 0) {
      return e;
    }
  }
  return null;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Server-provided error code (e.g. `replay_mismatch`), if any. */
  code: string | null;
  /** The URL that failed - useful for logging without parsing `message`. */
  endpoint: string;

  constructor(status: number, endpoint: string, body: unknown) {
    const code = extractErrorCode(body);
    const message = code
      ? `${endpoint} failed: ${status} ${code}`
      : `${endpoint} failed: ${status}`;
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = code;
    this.endpoint = endpoint;
  }
}
